import { ComponentPropsWithoutRef, forwardRef, Ref } from "react";
import { LayoutUtilProps, Size } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { ListItem } from "./ListItem";

import cx from "classnames";
import styles from "./List.module.scss";

/** Size option for list typography */
export type ListSize = Extract<Size, "small" | "medium" | "large">;

/**
 * Props for unordered list variant
 * @extends ComponentPropsWithoutRef<"ul">
 * @extends LayoutUtilProps
 */
export type UnorderedListProps = ComponentPropsWithoutRef<"ul"> &
  LayoutUtilProps & {
    /** Renders as unordered list (ul). Default. */
    variant?: "unordered";
    /** Typography size for list content. @default "medium" */
    size?: ListSize;
  };

/**
 * Props for ordered list variant
 * @extends ComponentPropsWithoutRef<"ol">
 * @extends LayoutUtilProps
 */
export type OrderedListProps = ComponentPropsWithoutRef<"ol"> &
  LayoutUtilProps & {
    /** Renders as ordered list (ol). */
    variant: "ordered";
    /** Typography size for list content. @default "medium" */
    size?: ListSize;
  };

/**
 * Props for the List component
 * @extends UnorderedListProps
 * @extends OrderedListProps
 */
export type ListProps = UnorderedListProps | OrderedListProps;

/**
 * List component for displaying ordered or unordered lists with design system styling.
 *
 * Features:
 * - Supports unordered (ul) and ordered (ol) lists via variant prop
 * - Typography size options aligned with design tokens
 * - Semantic HTML and accessible list structure
 * - Supports nested lists
 * - Layout utilities for positioning and spacing
 *
 * @example
 * <List>
 *   <List.Item>First item</List.Item>
 *   <List.Item>Second item</List.Item>
 * </List>
 *
 * @example
 * <List variant="ordered" start={1}>
 *   <List.Item>Step one</List.Item>
 *   <List.Item>Step two</List.Item>
 * </List>
 */
export const List = Object.assign(
  forwardRef<HTMLUListElement | HTMLOListElement, ListProps>(
    function ListInner(props, ref) {
      const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
      const {
        className,
        children,
        variant = "unordered",
        size = "medium",
        style,
        ...rest
      } = componentProps;

      const listClasses = cx(styles["list"], className, {
        [styles["unordered"]]: variant === "unordered",
        [styles["ordered"]]: variant === "ordered",
        [styles["small"]]: size === "small",
        [styles["large"]]: size === "large",
      });

      const styleCombined = {
        ...style,
        ...layoutStyles,
      };

      if (variant === "ordered") {
        return (
          <ol
            ref={ref as Ref<HTMLOListElement>}
            className={listClasses}
            style={styleCombined}
            data-anv="list"
            {...rest}
          >
            {children}
          </ol>
        );
      }

      return (
        <ul
          ref={ref as Ref<HTMLUListElement>}
          className={listClasses}
          style={styleCombined}
          data-anv="list"
          {...rest}
        >
          {children}
        </ul>
      );
    },
  ),
  {
    /**
     * ListItem component for individual items within a List.
     *
     * Features:
     * - Renders as a semantic list item (li)
     * - Supports layout and styling via standard HTML attributes
     *
     * @example
     * <List.Item>Item content</List.Item>
     */
    Item: ListItem,
  },
);
List.displayName = "List";
